import {
    forwardRef,
    useEffect,

    useState,
    useRef
} from "react";

import { ThemeProvider } from "../v2/home/theme/ThemeProvider";

import {
    ASSET_ITEM_URL,

} from "../../Constants/Constants";

import AxiosInstance from "../../services/AxiosInstance";
import { useAlert } from "../alerts/AlertContext";
import {
    Box,
    Typography,
    Chip,
    Divider,
    CircularProgress
} from "@mui/material";


function AssetActivity({ editAssetId, selectedAssetName }, ref) {

    const ACTIVITY_TITLES = {
        ASSET_CREATED: "Asset Onboarded",
        ASSET_UPDATED: "Asset Updated",
        ASSET_ASSIGNED: "Asset Assigned",
        ASSET_REASSIGNED: "Asset Reassigned",
        ORGANIZATION_CHANGED: "Organization Changed",
        STATUS_CHANGED: "Status Changed",
        MAINTENANCE_STARTED: "Maintenance Started",
        MAINTENANCE_COMPLETED: "Maintenance Completed",
        RESERVED: "Reserved",
        RESERVATION_CANCELLED: "Reservation Cancelled",
        ASSET_RETURNED: "Asset Returned",
        ASSET_LOST: "Asset Lost",
        ASSET_RECOVERED: "Asset Recovered",
        ASSET_RETIRED: "Asset Retired",
        DISPOSAL_INITIATED: "Disposal Initiated",
        ASSET_DISPOSED: "Asset Disposed",
        TICKET_LINKED: "Ticket Linked",
        TICKET_UNLINKED: "Ticket Unlinked",
        ATTACHMENT_ADDED: "Attachment Added",
        ATTACHMENT_REMOVED: "Attachment Removed",
        COMMENT_ADDED: "Comment Added",
        CONFIGURATION_UPDATED: "Configuration Updated",
        WARRANTY_UPDATED: "Warranty Updated",
        OWNERSHIP_CHANGED: "Ownership Changed"
    };

    const getActivityTitle = (type) =>
        ACTIVITY_TITLES[type] || type;

    const formatActivityDate = (date) => {

        if (!date) {
            return "";
        }

        return new Intl.DateTimeFormat(
            "en-IN",
            {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit"
            }
        ).format(new Date(date));
    };

    const [assetActivities, setAssetActivities] = useState([]);
    const [activitiesLoading, setActivitiesLoading] = useState(false);

    const fetchAssetActivities = async (assetId) => {
        try {
            setActivitiesLoading(true);

            const response = await AxiosInstance.get(
                `${ASSET_ITEM_URL}/${editAssetId}/activities`
            );

            setAssetActivities(response.data || []);

        } catch (error) {
            console.error(
                "Failed to load asset activities",
                error
            );

            setAssetActivities([]);

        } finally {
            setActivitiesLoading(false);
        }
    };

    useEffect(() => {

        if (editAssetId) {
            fetchAssetActivities(editAssetId);
        }

    }, [editAssetId]);


    return (
        <ThemeProvider>
            <Box sx={{ mt: 3 }}>

                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        mb: 2
                    }}
                >
                    Activity
                </Typography>

                {activitiesLoading ? (

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 4
                        }}
                    >
                        <CircularProgress size={28} />
                    </Box>

                ) : assetActivities.length === 0 ? (

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        No activity found.
                    </Typography>

                ) : (

                    <Box>

                        {assetActivities.map((activity, index) => (

                            <Box
                                key={activity.id}
                                sx={{
                                    display: "flex",
                                    gap: 2,
                                    mb: 2
                                }}
                            >

                                {/* Timeline dot */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center"
                                    }}
                                >

                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: "50%",
                                            bgcolor: "primary.main",
                                            mt: 1
                                        }}
                                    />

                                    {index <
                                        assetActivities.length - 1 && (
                                            <Box
                                                sx={{
                                                    width: "2px",
                                                    flexGrow: 1,
                                                    bgcolor: "divider",
                                                    mt: 0.5
                                                }}
                                            />
                                        )}

                                </Box>


                                {/* Activity */}
                                <Box
                                    sx={{
                                        flex: 1,
                                        pb: 2
                                    }}
                                >

                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "flex-start",
                                            gap: 2
                                        }}
                                    >

                                        <Typography
                                            variant="subtitle2"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {getActivityTitle(
                                                activity.activityType
                                            )}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {formatActivityDate(
                                                activity.changedOn
                                            )}
                                        </Typography>

                                    </Box>


                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.5 }}
                                    >
                                        {activity.activityDescription}
                                    </Typography>


                                    {activity.changedBy && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: "block", mt: 0.5 }}
                                        >
                                            By {activity.changedBy}
                                        </Typography>
                                    )}

                                </Box>

                            </Box>

                        ))}

                    </Box>

                )}

            </Box>
        </ThemeProvider>
    );
}

export default forwardRef(AssetActivity);
